/**
 * Token Transaction History Component (Tier 5.1.4)
 * 
 * Displays a comprehensive history of all token transactions including:
 * - Purchases (bought tokens)
 * - Redemptions (spent tokens)
 * - Bonuses (received tokens)
 * - Transfers (sent/received)
 * 
 * Features:
 * - Filterable by transaction type and date range
 * - Searchable by description or business
 * - Pagination for large histories
 * - Export functionality
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHistory, 
  faCoins, 
  faShoppingCart, 
  faGift, 
  faStore,
  faFilter,
  faSearch,
  faDownload,
  faCalendarAlt,
  faArrowUp,
  faArrowDown,
  faSpinner,
  faReceipt,
  faExchangeAlt,
  faTimes,
  faInfoCircle,
  faCheckCircle,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import { collection, query, where, orderBy, limit, startAfter, getDocs } from 'firebase/firestore';
import { firestore } from '../FirebaseConfig';
import { useToast } from './ui';
import { formatUSVITime, formatShortDate } from '../utils/dateUtils';
import { BRAND, STATUS, INK } from '../styles/colors';
import '../styles/TokenHistory.css';

// Transaction type configurations
const TRANSACTION_TYPES = {
  purchase: {
    icon: faShoppingCart,
    label: 'Purchase',
    color: STATUS.success,
    prefix: '+'
  },
  redemption: {
    icon: faStore,
    label: 'Redeemed',
    color: BRAND.brass,
    prefix: '-'
  },
  spend_redeem: {
    icon: faStore,
    label: 'Redeemed',
    color: BRAND.brass,
    prefix: '-'
  },
  bonus: {
    icon: faGift,
    label: 'Bonus',
    color: BRAND.brassLight,
    prefix: '+'
  },
  transfer_in: {
    icon: faArrowDown,
    label: 'Received',
    color: BRAND.navyLight,
    prefix: '+'
  },
  transfer_out: {
    icon: faArrowUp,
    label: 'Sent',
    color: BRAND.oxblood,
    prefix: '-'
  },
  refund: {
    icon: faExchangeAlt,
    label: 'Refund',
    color: INK.muted,
    prefix: '+'
  },
  refund_redeem: {
    icon: faExchangeAlt,
    label: 'Redemption refunded',
    color: INK.muted,
    prefix: '+'
  },
  expired: {
    icon: faClock,
    label: 'Expired',
    color: BRAND.oxbloodDark,
    prefix: '-'
  }
};

const ITEMS_PER_PAGE = 10;

// Transaction types that increase the user's token balance.
const POSITIVE_TYPES = ['purchase', 'bonus', 'transfer_in', 'refund', 'refund_redeem'];
const REDEMPTION_TYPES = ['redemption', 'spend_redeem'];

/**
 * Main Token History Component
 */
function TokenHistory({ userId, embedded = false, maxItems = null }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);
  const [, setCurrentPage] = useState(1);
  
  // Filters
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Detail modal
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  
  const { showToast } = useToast();

  // Load transactions
  const loadTransactions = useCallback(async (reset = false) => {
    if (!userId) return;
    
    setLoading(true);
    
    try {
      const transactionsRef = collection(firestore, 'tokenTransactions');
      let constraints = [
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      ];
      
      // Apply type filter
      if (typeFilter !== 'all') {
        constraints.push(where('type', '==', typeFilter));
      }
      
      // Apply date filter
      if (dateFilter !== 'all') {
        const now = new Date();
        let startDate;
        
        switch (dateFilter) {
          case 'today':
            startDate = new Date(now.setHours(0, 0, 0, 0));
            break;
          case 'week':
            startDate = new Date(now.setDate(now.getDate() - 7));
            break;
          case 'month':
            startDate = new Date(now.setMonth(now.getMonth() - 1));
            break;
          case 'year':
            startDate = new Date(now.setFullYear(now.getFullYear() - 1));
            break;
          default:
            startDate = null;
        }
        
        if (startDate) {
          constraints.push(where('createdAt', '>=', startDate));
        }
      }
      
      // Add pagination
      constraints.push(limit(maxItems || ITEMS_PER_PAGE));
      
      if (!reset && lastDoc) {
        constraints.push(startAfter(lastDoc));
      }
      
      const q = query(transactionsRef, ...constraints);
      const snapshot = await getDocs(q);
      
      const newTransactions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      if (reset) {
        setTransactions(newTransactions);
      } else {
        setTransactions(prev => [...prev, ...newTransactions]);
      }
      
      setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMore(snapshot.docs.length === (maxItems || ITEMS_PER_PAGE));
      
    } catch (error) {
      console.error('Error loading transactions:', error);
      showToast('Failed to load transaction history', 'error');
    } finally {
      setLoading(false);
    }
  }, [userId, typeFilter, dateFilter, maxItems, lastDoc, showToast]);

  // Load on mount and filter change
  useEffect(() => {
    loadTransactions(true);
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, typeFilter, dateFilter]);

  // Filter transactions by search query
  const filteredTransactions = useMemo(() => {
    if (!searchQuery.trim()) return transactions;
    
    const query = searchQuery.toLowerCase();
    return transactions.filter(t => 
      t.description?.toLowerCase().includes(query) ||
      t.businessName?.toLowerCase().includes(query) ||
      t.id.toLowerCase().includes(query)
    );
  }, [transactions, searchQuery]);

  // Calculate summary stats
  const stats = useMemo(() => {
    const result = {
      totalIn: 0,
      totalOut: 0,
      purchaseCount: 0,
      redemptionCount: 0
    };
    
    transactions.forEach(t => {
      const amount = t.amount || 0;
      if (POSITIVE_TYPES.includes(t.type)) {
        result.totalIn += amount;
      } else {
        result.totalOut += Math.abs(amount);
      }
      
      if (t.type === 'purchase') result.purchaseCount++;
      if (REDEMPTION_TYPES.includes(t.type)) result.redemptionCount++;
    });
    
    return result;
  }, [transactions]);

  // Export transactions
  const handleExport = useCallback(() => {
    const csvContent = [
      ['Date', 'Type', 'Amount', 'Description', 'Business', 'Transaction ID'].join(','),
      ...filteredTransactions.map(t => [
        formatUSVITime(t.createdAt?.toDate?.() || t.createdAt).replace(/,/g, ''),
        TRANSACTION_TYPES[t.type]?.label || t.type,
        t.amount,
        (t.description || '').replace(/,/g, ';'),
        (t.businessName || '').replace(/,/g, ';'),
        t.id
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `token-history-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    showToast('Transaction history exported', 'success');
  }, [filteredTransactions, showToast]);

  // Load more
  const handleLoadMore = () => {
    loadTransactions(false);
  };

  if (embedded && loading && transactions.length === 0) {
    return (
      <div className="token-history-loading">
        <FontAwesomeIcon icon={faSpinner} spin />
        <span>Loading history...</span>
      </div>
    );
  }

  return (
    <div className={`token-history ${embedded ? 'embedded' : ''}`}>
      {!embedded && (
        <>
          <div className="history-header">
            <div className="header-title">
              <FontAwesomeIcon icon={faHistory} />
              <h2>Transaction History</h2>
            </div>
            
            <div className="header-actions">
              <button 
                className={`filter-toggle ${showFilters ? 'active' : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <FontAwesomeIcon icon={faFilter} />
                Filters
              </button>
              
              <button className="export-btn" onClick={handleExport}>
                <FontAwesomeIcon icon={faDownload} />
                Export
              </button>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="history-stats">
            <div className="stat-card">
              <div className="stat-icon earned">
                <FontAwesomeIcon icon={faArrowDown} />
              </div>
              <div className="stat-info">
                <span className="stat-value">+{stats.totalIn}</span>
                <span className="stat-label">Doubloons Earned</span>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon spent">
                <FontAwesomeIcon icon={faArrowUp} />
              </div>
              <div className="stat-info">
                <span className="stat-value">-{stats.totalOut}</span>
                <span className="stat-label">Doubloons Spent</span>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon purchases">
                <FontAwesomeIcon icon={faShoppingCart} />
              </div>
              <div className="stat-info">
                <span className="stat-value">{stats.purchaseCount}</span>
                <span className="stat-label">Purchases</span>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon redemptions">
                <FontAwesomeIcon icon={faStore} />
              </div>
              <div className="stat-info">
                <span className="stat-value">{stats.redemptionCount}</span>
                <span className="stat-label">Redemptions</span>
              </div>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="history-filters">
              <div className="filter-group">
                <label>Type</label>
                <select 
                  value={typeFilter} 
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <option value="all">All Types</option>
                  {Object.entries(TRANSACTION_TYPES).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="filter-group">
                <label>Date Range</label>
                <select 
                  value={dateFilter} 
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                  <option value="year">Last Year</option>
                </select>
              </div>
              
              <div className="filter-group search">
                <label>Search</label>
                <div className="search-input">
                  <FontAwesomeIcon icon={faSearch} />
                  <input
                    type="text"
                    placeholder="Search transactions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')}>
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Transactions List */}
      <div className="transactions-list">
        {filteredTransactions.length === 0 ? (
          <div className="no-transactions">
            <FontAwesomeIcon icon={faReceipt} />
            <p>No transactions found</p>
            {(typeFilter !== 'all' || dateFilter !== 'all' || searchQuery) && (
              <button onClick={() => {
                setTypeFilter('all');
                setDateFilter('all');
                setSearchQuery('');
              }}>
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <>
            {filteredTransactions.map(transaction => (
              <TransactionItem
                key={transaction.id}
                transaction={transaction}
                onClick={() => setSelectedTransaction(transaction)}
              />
            ))}
            
            {hasMore && (
              <button 
                className="load-more-btn"
                onClick={handleLoadMore}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Loading...
                  </>
                ) : (
                  'Load More'
                )}
              </button>
            )}
          </>
        )}
      </div>

      {/* Transaction Detail Modal */}
      {selectedTransaction && (
        <TransactionDetailModal
          transaction={selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
        />
      )}
    </div>
  );
}

/**
 * Individual Transaction Item
 */
function TransactionItem({ transaction, onClick }) {
  const config = TRANSACTION_TYPES[transaction.type] || TRANSACTION_TYPES.purchase;
  const isPositive = POSITIVE_TYPES.includes(transaction.type);
  const displayAmount = Math.abs(transaction.amount || 0);
  
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return formatShortDate(date);
  };

  return (
    <div className="transaction-item" onClick={onClick}>
      <div 
        className="transaction-icon"
        style={{ backgroundColor: `${config.color}15`, color: config.color }}
      >
        <FontAwesomeIcon icon={config.icon} />
      </div>
      
      <div className="transaction-info">
        <div className="transaction-main">
          <span className="transaction-label">{config.label}</span>
          {transaction.businessName && (
            <span className="transaction-business">
              <FontAwesomeIcon icon={faStore} />
              {transaction.businessName}
            </span>
          )}
        </div>
        <div className="transaction-meta">
          <span className="transaction-date">
            <FontAwesomeIcon icon={faCalendarAlt} />
            {formatDate(transaction.createdAt)}
          </span>
          {transaction.description && (
            <span className="transaction-desc">{transaction.description}</span>
          )}
        </div>
      </div>
      
      <div className={`transaction-amount ${isPositive ? 'positive' : 'negative'}`}>
        <FontAwesomeIcon icon={faCoins} />
        <span>{config.prefix}{displayAmount}</span>
      </div>
    </div>
  );
}

/**
 * Transaction Detail Modal
 */
function TransactionDetailModal({ transaction, onClose }) {
  const config = TRANSACTION_TYPES[transaction.type] || TRANSACTION_TYPES.purchase;
  const isPositive = POSITIVE_TYPES.includes(transaction.type);
  const displayAmount = Math.abs(transaction.amount || 0);
  
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return formatUSVITime(date);
  };

  return (
    <div className="transaction-detail-overlay" onClick={onClose}>
      <div className="transaction-detail-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        
        <div 
          className="detail-header"
          style={{ backgroundColor: config.color }}
        >
          <FontAwesomeIcon icon={config.icon} />
          <span>{config.label}</span>
        </div>
        
        <div className="detail-content">
          <div className={`detail-amount ${isPositive ? 'positive' : 'negative'}`}>
            <span className="amount-prefix">{config.prefix}</span>
            <span className="amount-value">{displayAmount}</span>
            <FontAwesomeIcon icon={faCoins} />
          </div>
          
          <div className="detail-rows">
            <div className="detail-row">
              <label>Transaction ID</label>
              <span className="mono">{transaction.id}</span>
            </div>
            
            <div className="detail-row">
              <label>Date & Time</label>
              <span>{formatDate(transaction.createdAt)}</span>
            </div>
            
            <div className="detail-row">
              <label>Status</label>
              <span className="status-badge completed">
                <FontAwesomeIcon icon={faCheckCircle} />
                Completed
              </span>
            </div>
            
            {transaction.description && (
              <div className="detail-row">
                <label>Description</label>
                <span>{transaction.description}</span>
              </div>
            )}
            
            {transaction.businessName && (
              <div className="detail-row">
                <label>Business</label>
                <span>{transaction.businessName}</span>
              </div>
            )}
            
            {transaction.offerName && (
              <div className="detail-row">
                <label>Offer</label>
                <span>{transaction.offerName}</span>
              </div>
            )}
            
            {transaction.redemptionCode && (
              <div className="detail-row">
                <label>Redemption Code</label>
                <span className="mono">{transaction.redemptionCode}</span>
              </div>
            )}
            
            {transaction.packageName && (
              <div className="detail-row">
                <label>Package</label>
                <span>{transaction.packageName}</span>
              </div>
            )}
            
            {transaction.paymentMethod && (
              <div className="detail-row">
                <label>Payment Method</label>
                <span>{transaction.paymentMethod}</span>
              </div>
            )}
            
            <div className="detail-row">
              <label>Balance After</label>
              <span className="balance-after">
                <FontAwesomeIcon icon={faCoins} />
                {transaction.balanceAfter || 'N/A'}
              </span>
            </div>
          </div>
          
          <div className="detail-footer">
            <FontAwesomeIcon icon={faInfoCircle} />
            <span>Transaction records are kept for your reference and cannot be modified.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Compact Token History Widget for embedding
 */
export function TokenHistoryWidget({ userId, maxItems = 5, onViewAll }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadRecentTransactions = async () => {
      if (!userId) return;
      
      try {
        const transactionsRef = collection(firestore, 'tokenTransactions');
        const q = query(
          transactionsRef,
          where('userId', '==', userId),
          orderBy('createdAt', 'desc'),
          limit(maxItems)
        );
        
        const snapshot = await getDocs(q);
        setTransactions(snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })));
      } catch (error) {
        console.error('Error loading recent transactions:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadRecentTransactions();
  }, [userId, maxItems]);

  if (loading) {
    return (
      <div className="history-widget loading">
        <FontAwesomeIcon icon={faSpinner} spin />
      </div>
    );
  }

  return (
    <div className="history-widget">
      <div className="widget-header">
        <h4>
          <FontAwesomeIcon icon={faHistory} />
          Recent Activity
        </h4>
        {onViewAll && (
          <button className="view-all-btn" onClick={onViewAll}>
            View All
          </button>
        )}
      </div>
      
      {transactions.length === 0 ? (
        <div className="widget-empty">
          <p>No recent transactions</p>
        </div>
      ) : (
        <div className="widget-list">
          {transactions.map(transaction => {
            const config = TRANSACTION_TYPES[transaction.type] || TRANSACTION_TYPES.purchase;
            const isPositive = POSITIVE_TYPES.includes(transaction.type);
            const displayAmount = Math.abs(transaction.amount || 0);
            
            return (
              <div key={transaction.id} className="widget-item">
                <div 
                  className="widget-item-icon"
                  style={{ color: config.color }}
                >
                  <FontAwesomeIcon icon={config.icon} />
                </div>
                <div className="widget-item-info">
                  <span className="widget-item-label">{config.label}</span>
                  <span className="widget-item-date">
                    {formatShortDate(transaction.createdAt?.toDate?.() || transaction.createdAt)}
                  </span>
                </div>
                <div className={`widget-item-amount ${isPositive ? 'positive' : 'negative'}`}>
                  {config.prefix}{displayAmount}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TokenHistory;
